import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Loader2, Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle, X, Image as ImageIcon, Phone, MapPin, Linkedin, Facebook } from 'lucide-react';

export default function PerplexityALMLogin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  useEffect(() => {
    if (user && !loading) {
      navigate('/perplexity-alm/dashboard');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);
      setError(null);

      // Use a more reliable default logo
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setLogoLoading(false);
          return;
        }

        if (data?.value) {
          // Test if the image loads before setting it
          const img = new Image();
          img.onload = () => {
            setLogoUrl(data.value);
            setLogoLoading(false);
          };
          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setLogoLoading(false);
          };
          img.src = data.value;
        } else {
          setLogoLoading(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setLogoLoading(false);
      }
    } catch (err) {
      setLogoLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) throw error;

      if (data.user) {
        navigate('/perplexity-alm/dashboard');
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Please enter your email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/perplexity-alm/reset-password`
      });

      if (error) throw error;

      setResetEmailSent(true);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const TermsModal = ({ isOpen, onClose, title, content }: { 
    isOpen: boolean; 
    onClose: () => void; 
    title: string; 
    content: React.ReactNode; 
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-xl max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {content}
          </div>
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const termsContent = (
    <div className="prose prose-gray max-w-none text-gray-700">
      <h3 className="text-xl font-semibold mb-4">Terms of Service for PerplexityALM</h3>
      
      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">1. Service Agreement</h4>
        <p className="mb-4">
          By creating an account for PerplexityALM, you agree to use our Application Life-Cycle Management platform to streamline your testing and development workflows.
        </p>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">2. Platform Usage</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Create and manage test plans and test runs</li>
          <li>Collaborate with team members on quality assurance</li>
          <li>Track project progress and testing metrics</li>
          <li>Maintain data security and access controls</li>
        </ul>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">3. Data Security</h4>
        <p className="mb-4">
          PerplexityALM implements enterprise-grade security measures to protect your testing data and project information. All data is encrypted and stored securely.
        </p>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">4. Subscription Terms</h4>
        <p className="mb-4">
          Access to PerplexityALM may require a subscription. Payment terms and feature access are based on your selected plan.
        </p>
      </section>
    </div>
  );

  const privacyContent = (
    <div className="prose prose-gray max-w-none text-gray-700">
      <h3 className="text-xl font-semibold mb-4">Privacy Policy for PerplexityALM</h3>
      
      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">1. Information We Collect</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Account information (name, email, company details)</li>
          <li>Test plans, test cases, and testing data</li>
          <li>Project metrics and analytics</li>
          <li>Platform usage and performance data</li>
        </ul>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">2. How We Use Your Information</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide PerplexityALM platform services</li>
          <li>Generate testing insights and analytics</li>
          <li>Improve platform performance and features</li>
          <li>Provide customer support and assistance</li>
        </ul>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">3. Data Protection</h4>
        <p className="mb-4">
          We implement industry-standard security measures including encryption, access controls, and regular security audits to protect your testing data.
        </p>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">4. Your Rights</h4>
        <p className="mb-4">
          You have full control over your testing data and can export, modify, or delete your information at any time through the platform.
        </p>
      </section>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Perplexity Logo */}
      <header className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex-1 flex items-center justify-between">
              <Link to="/" className="text-white hover:text-primary transition-colors -mt-4 -mb-4">
                {logoLoading ? (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : logoUrl ? (
                  <>
                    <img 
                      src={logoUrl} 
                      className="h-32 md:h-28 w-auto object-contain max-w-[500px] md:max-w-[500px]"
                      onError={(e) => {
                        console.warn('Error loading logo image, falling back to text');
                        // Hide the image and show text fallback
                        e.currentTarget.style.display = 'none';
                        const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (textFallback) {
                          textFallback.style.display = 'block';
                        }
                      }}
                    />
                    <div 
                      className="h-32 md:h-28 flex items-center justify-center text-primary text-2xl font-bold"
                      style={{ display: 'none' }}
                    >
                      PERPLEXITY
                    </div>
                  </>
                ) : (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center text-primary">
                    <ImageIcon size={32} />
                  </div>
                )}
              </Link>
              <button 
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 pt-32">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {forgotPasswordMode ? 'Reset Password' : 'PerplexityALM Login'}
              </h1>
              <p className="text-gray-600">
                {forgotPasswordMode 
                  ? 'Enter your email to receive a password reset link'
                  : 'Access your PerplexityALM account'
                }
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {resetEmailSent && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg mb-6 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="text-sm">Password reset email sent! Check your inbox.</span>
              </div>
            )}

            <form onSubmit={forgotPasswordMode ? handleForgotPassword : handleLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 bg-white"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {!forgotPasswordMode && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 bg-white"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              {!forgotPasswordMode && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="accept-terms"
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="h-4 w-4 text-[#40E0D0] focus:ring-[#40E0D0] border-gray-300 rounded"
                    />
                    <label htmlFor="accept-terms" className="ml-2 block text-sm text-gray-700">
                      I accept the{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-[#40E0D0] hover:text-[#40E0D0]/80 underline"
                      >
                        Terms of Service
                      </button>
                      {' '}and{' '}
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-[#40E0D0] hover:text-[#40E0D0]/80 underline"
                      >
                        Privacy Policy
                      </button>
                    </label>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || (!forgotPasswordMode && !acceptTerms)}
                className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-5 w-5" />
                    {forgotPasswordMode ? 'Sending...' : 'Signing In...'}
                  </>
                ) : (
                  forgotPasswordMode ? 'Send Reset Email' : 'Sign In'
                )}
              </button>

              {!forgotPasswordMode && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setForgotPasswordMode(true)}
                    className="text-[#40E0D0] hover:text-[#40E0D0]/80 text-sm transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {forgotPasswordMode && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotPasswordMode(false);
                      setResetEmailSent(false);
                      setError('');
                    }}
                    className="text-[#40E0D0] hover:text-[#40E0D0]/80 text-sm transition-colors"
                  >
                    Back to Login
                  </button>
                </div>
              )}
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Need access to PerplexityALM?{' '}
                <Link 
                  to="/requestaccess" 
                  className="text-[#40E0D0] hover:text-[#40E0D0]/80 font-medium transition-colors"
                >
                  Request access here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <img src={logoUrl || 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format'} alt="Perplexity" className="h-12 w-auto mb-4" />
              <p className="text-gray-400 text-sm">
                A Registered Trademark of Perplexity Solved Solutions Inc
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-2">
                <a href="mailto:info@perplexityonline.com" 
                   className="text-gray-400 hover:text-white flex items-center group">
                  <Mail className="w-4 h-4 mr-2 text-primary group-hover:text-white transition-colors" />
                  info@perplexityonline.com
                </a>
                <a href="tel:18663078708" 
                   className="text-gray-400 hover:text-white flex items-center group">
                  <Phone className="w-4 h-4 mr-2 text-primary group-hover:text-white transition-colors" />
                  1 (866) 307-8708
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Address</h4>
              <div className="text-gray-400 flex items-start">
                <MapPin className="w-4 h-4 mr-2 mt-1 text-primary" />
                <p>
                  Headquarters<br />
                  5851 Legacy Cir<br />
                  Plano, Texas 75024
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a 
                  href="https://www.linkedin.com/company/perplexityhq/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-linkedin"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#0077B5] to-[#00A0DC] hover:scale-110 transition-transform">
                    <Linkedin className="w-5 h-5 text-white" />
                  </div>
                </a>
                <a 
                  href="https://x.com/PerplexityHQ" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-x"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#000000] to-[#333333] hover:scale-110 transition-transform">
                    <X className="w-5 h-5 text-white" />
                  </div>
                </a>
                <a 
                  href="https://www.facebook.com/Perplexitymeet/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-facebook"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#1877F2] to-[#00B2FF] hover:scale-110 transition-transform">
                    <Facebook className="w-5 h-5 text-white" />
                  </div>
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                PERPLEXITY™, all rights reserved.
              </p>
              <div className="flex items-center space-x-6 mt-4 md:mt-0">
                <Link to="/terms" className="text-[#40E0D0] hover:text-[#0066FF] text-sm transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-[#40E0D0] hover:text-[#0066FF] text-sm transition-colors">
                  Privacy Statement
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms and Privacy Modals */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
        content={termsContent}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
        content={privacyContent}
      />
    </div>
  );
}